// frontend/src/utils/reference/entityStateHelpers.js

function normalizeText(value = '') {
  return String(value || '').toLowerCase().trim();
}

function normalizeWhitespace(value = '') {
  return String(value || '').replace(/\s+/g, ' ').trim();
}

function normalizeRole(value = '') {
  return normalizeWhitespace(value).toLowerCase();
}

function buildDisplayName(entity = {}) {
  const explicitName = normalizeWhitespace(entity.name || '');

  if (explicitName) {
    return explicitName;
  }

  const firstName = normalizeWhitespace(entity.first_name || entity.firstName || '');
  const lastName = normalizeWhitespace(entity.last_name || entity.lastName || '');

  return [firstName, lastName].filter(Boolean).join(' ').trim();
}

function buildEntityKey(entity = {}) {
  const entityId = normalizeWhitespace(entity.entityId || entity?.metadata?.entityId || '');
  if (entityId) {
    return `id:${entityId}`;
  }

  const type = normalizeText(entity.type || 'person') || 'person';
  const name = normalizeText(buildDisplayName(entity));
  const role = normalizeRole(
    entity.role ||
      entity.relationship ||
      entity.relationship_type ||
      entity.relationshipType ||
      ''
  );

  if (!name) {
    return null;
  }

  return `${type}:${name}${role ? `:${role}` : ''}`;
}

function createEmptyEntityState() {
  return {
    active: null,
    previous: null,
    related: [],
    named: [],
    historical: [],
    candidates: [],
  };
}

function createEntityCandidate(entity = {}, options = {}) {
  const name = buildDisplayName(entity);
  const role =
    normalizeWhitespace(
      entity.role ||
        entity.relationship ||
        entity.relationship_type ||
        entity.relationshipType ||
        ''
    ) || null;

  const firstName =
    normalizeWhitespace(entity.first_name || entity.firstName || '') || null;
  const lastName =
    normalizeWhitespace(entity.last_name || entity.lastName || '') || null;

  const type = normalizeText(entity.type || 'person') || 'person';
  const key = buildEntityKey({
    ...entity,
    name,
    role,
    type,
  });

  if (!name || !key) {
    return null;
  }

  return {
    key,
    entityId: normalizeWhitespace(entity.entityId || entity?.metadata?.entityId || '') || null,
    type,
    name,
    firstName,
    lastName,
    role,
    source: options.source || entity.source || 'unknown',
    score: Number.isFinite(options.score) ? options.score : 0,
    roles: Array.isArray(options.roles) ? [...new Set(options.roles)] : [],
    signals: {
      explicitlyNamed: Boolean(options.explicitlyNamed),
      pronounSupported: Boolean(options.pronounSupported),
      carryForward: Boolean(options.carryForward),
      corrected: Boolean(options.corrected),
      historical: Boolean(options.historical),
      related: Boolean(options.related),
      ...entity.signals,
      ...options.signals,
    },
    metadata: {
      relationshipId:
        entity.relationship_id ||
        entity.relationshipId ||
        entity.id ||
        null,
      ...entity.metadata,
      ...options.metadata,
    },
  };
}

function cloneEntityCandidate(entity = null) {
  if (!entity) return null;

  return {
    ...entity,
    roles: Array.isArray(entity.roles) ? [...entity.roles] : [],
    signals: {
      ...(entity.signals || {}),
    },
    metadata: {
      ...(entity.metadata || {}),
    },
  };
}

function sameEntity(a = null, b = null) {
  if (!a || !b) return false;

  const aKey = a.key || buildEntityKey(a);
  const bKey = b.key || buildEntityKey(b);

  if (!aKey || !bKey) return false;

  return aKey === bKey;
}

function upsertEntityCandidate(list = [], entity = null) {
  if (!entity) return Array.isArray(list) ? [...list] : [];

  const next = Array.isArray(list) ? [...list] : [];
  const index = next.findIndex((item) => sameEntity(item, entity));

  if (index === -1) {
    next.push(cloneEntityCandidate(entity));
    return next;
  }

  const existing = next[index];

  next[index] = {
    ...existing,
    ...entity,
    score: Math.max(existing?.score || 0, entity?.score || 0),
    roles: [...new Set([...(existing?.roles || []), ...(entity?.roles || [])])],
    signals: {
      ...(existing?.signals || {}),
      ...(entity?.signals || {}),
    },
    metadata: {
      ...(existing?.metadata || {}),
      ...(entity?.metadata || {}),
    },
  };

  return next;
}

function dedupeEntities(list = []) {
  if (!Array.isArray(list) || list.length === 0) return [];

  return list.reduce((acc, entity) => upsertEntityCandidate(acc, entity), []);
}

function addRole(entity = null, role = '') {
  if (!entity || !role) return entity;

  const next = cloneEntityCandidate(entity);
  next.roles = [...new Set([...(next.roles || []), role])];
  return next;
}

function sortEntitiesByScore(list = []) {
  if (!Array.isArray(list)) return [];

  return [...list].sort((a, b) => {
    const scoreDelta = (b?.score || 0) - (a?.score || 0);
    if (scoreDelta !== 0) return scoreDelta;

    return String(a?.name || '').localeCompare(String(b?.name || ''));
  });
}

function limitEntities(list = [], limit = 3) {
  if (!Array.isArray(list) || limit <= 0) return [];
  return list.slice(0, limit);
}

function toHistoricalEntities(relationships = [], excluded = []) {
  if (!Array.isArray(relationships) || relationships.length === 0) {
    return [];
  }

  return relationships
    .map((relationship) =>
      createEntityCandidate(relationship, {
        source: 'relationship_memory',
        signals: {
          historical: true,
        },
      })
    )
    .filter(Boolean)
    .filter(
      (candidate) => !excluded.some((entity) => sameEntity(candidate, entity))
    );
}

export {
  normalizeText,
  normalizeWhitespace,
  normalizeRole,
  buildDisplayName,
  buildEntityKey,
  createEmptyEntityState,
  createEntityCandidate,
  cloneEntityCandidate,
  sameEntity,
  upsertEntityCandidate,
  dedupeEntities,
  addRole,
  sortEntitiesByScore,
  limitEntities,
  toHistoricalEntities,
};